import { CaretLeft, CheckCircle, Info } from "@phosphor-icons/react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as yup from "yup";
import CountryInput from "../../../components/CountryInput";
import { useAuth } from "../../../context/AuthContext";
import useResolveGeo from "../../../hooks/useResolveGeo";
import {
  useSaveTravelDocument,
  useTravelDocument,
} from "../../../hooks/useTravelDocument";
import { leaveSetupTask } from "../../../utils/setupTaskReturn";

const schema = yup.object().shape({
  number: yup
    .string()
    .required("Passport number is required")
    // Passport numbers vary far too much by country for a tighter rule; 6-12
    // alphanumerics covers every ICAO-compliant document without rejecting a
    // real one.
    .matches(/^[A-Za-z0-9]{6,12}$/, "Enter a valid passport number"),
  expires_on: yup
    .date()
    .required("Expiry date is required")
    .min(new Date(), "This passport has expired"),
});

const fieldClass =
  "rounded-lg border border-gray-300 shadow px-4 py-3 w-full focus:outline-none focus-within:outline-deepBlue";
const labelClass = "text-sm font-bold text-gray-700";

/**
 * Account-level passport capture for international-resident KYC. It is not
 * passenger data and is never attached to the airline booking payload.
 *
 * The verification story is deliberately honest here. The only document check
 * Visaro has configured is YouVerify's Nigerian passport endpoint, so a
 * passport from anywhere else is stored unverified and the screen says so
 * rather than showing a bare badge the customer can do nothing about. Closing
 * that gap needs a provider with international document coverage.
 */
const TravelDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { fromSetup?: boolean; returnTo?: string } | null;
  const fromSetup = state?.fromSetup;
  const { user, refetch } = useAuth();
  const { country: detectedCountry } = useResolveGeo();
  const { passport, isLoadingPassport } = useTravelDocument();

  const [issuingCountry, setIssuingCountry] = useState(
    passport?.issuing_country ?? user?.residence_country ?? detectedCountry ?? "NG"
  );
  const [document, setDocument] = useState<File | null>(null);
  const [countryEdited, setCountryEdited] = useState(false);
  useEffect(() => {
    if (!countryEdited) {
      setIssuingCountry(passport?.issuing_country ?? user?.residence_country ?? detectedCountry ?? "NG");
    }
  }, [passport?.issuing_country, user?.residence_country, detectedCountry, countryEdited]);

  const { savePassport, savingPassport } = useSaveTravelDocument({
    onSuccess() {
      toast.success("Passport details saved");
      refetch();
      leaveSetupTask(navigate, state, fromSetup ? "/home" : "/home/settings/compliance");
    },
    onError(error) {
      toast.error(error?.message || "Could not save your passport details");
    },
  });

  return (
    <div className="bg-gray-50 p-5 md:p-10 flex items-center justify-center">
      <div className="flex flex-col gap-8 max-w-4xl w-full">
        <div className="flex gap-2 flex-col">
          <div className="flex items-center gap-2">
            <div
              onClick={() => navigate(-1)}
              className="p-2 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 cursor-pointer hover:bg-gray-100"
            >
              <CaretLeft weight="bold" />
              <span>Back</span>
            </div>
          </div>
          <div className="text-2xl font-bold">International Passport</div>
          <div>
            <span className="text-sm text-gray-500">
              International customers must submit a passport as an account KYC
              step before using Pay in 3. Enter the details exactly as shown and upload
              a clear copy of the document.
            </span>
          </div>
        </div>

        {passport?.verified && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex gap-3 items-start">
            <CheckCircle weight="fill" className="size-5 shrink-0 mt-0.5" />
            <span className="text-sm">
              This passport has been verified. Saving new details will replace it.
            </span>
          </div>
        )}

        <div className="bg-white rounded-lg w-full shadow-sm flex-col gap-5 flex p-5">
          {!isLoadingPassport && <Formik
            initialValues={{
              number: passport?.number ?? "",
              expires_on: passport?.expires_on ?? "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              savePassport({
                number: values.number.trim().toUpperCase(),
                issuing_country: issuingCountry,
                expires_on: values.expires_on,
                document: document as File,
              });
            }}
          >
            {() => (
              <Form className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>Country of issue*</label>
                  <CountryInput selected={issuingCountry} onSelect={(country) => {
                    setCountryEdited(true);
                    setIssuingCountry(country);
                  }} />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="document" className={labelClass}>Passport file*</label>
                  <input
                    id="document"
                    type="file"
                    required
                    accept="image/jpeg,image/png,application/pdf"
                    onChange={(event) => setDocument(event.target.files?.[0] ?? null)}
                    className={fieldClass}
                  />
                  <span className="text-xs text-gray-500">JPG, PNG, or PDF. Maximum 10 MB.</span>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="number" className={labelClass}>
                      Passport number*
                    </label>
                    <Field
                      id="number"
                      name="number"
                      placeholder="e.g. A01234567"
                      className={`${fieldClass} uppercase`}
                      autoComplete="off"
                    />
                    <ErrorMessage name="number" component="div" className="text-red-500 text-sm" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="expires_on" className={labelClass}>
                      Expiry date*
                    </label>
                    <Field
                      id="expires_on"
                      name="expires_on"
                      type="date"
                      className={fieldClass}
                    />
                    <ErrorMessage
                      name="expires_on"
                      component="div"
                      className="text-red-500 text-sm"
                    />
                  </div>
                </div>

                {issuingCountry && issuingCountry !== "NG" && (
                  <div className="bg-gray-50 border border-gray-200 text-gray-600 rounded-lg p-4 flex gap-3 items-start">
                    <Info className="size-5 shrink-0 mt-0.5" />
                    <span className="text-sm">
                      We store passports issued outside Nigeria without an
                      automated check, so your details may be reviewed manually.
                      Please make sure they match your document exactly.
                    </span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={savingPassport || isLoadingPassport || !document}
                  className="bg-visaro-orange text-white rounded-xl px-8 py-3 font-medium self-start disabled:opacity-60"
                >
                  {savingPassport ? "Uploading..." : "Submit passport"}
                </button>
              </Form>
            )}
          </Formik>}
        </div>
      </div>
    </div>
  );
};

export default TravelDocument;
